// Removed LocationDropdown import
// import LocationDropdown from '../../Dropdown/LocationName';

// Import necessary dependencies for React hooks, API, and components
import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../api'; // Custom API module for making HTTP requests
import Pagination from '../Tools/Pagination'; // Component for pagination controls
import MedicalAddModal from '../tabs/modals/Medicale/MedicalAddModal'; // Modal component for adding/editing medical centers

// MedicalTab component for managing EMS medical centers
const MedicalTab = () => {
  // State management
  const [medicalCenters, setMedicalCenters] = useState([]); // Stores list of medical centers
  const [formData, setFormData] = useState({
    id: '', // Medical center ID
    name: '', // Medical center name
    type: '', // Medical center type
    // location_id: '', // Removed location_id
    // area_id: ''      // Removed area_id
  }); // Form data for medical center creation/editing
  const [isEditMode, setIsEditMode] = useState(false); // Tracks if modal is in edit mode
  const [isLoading, setIsLoading] = useState(false); // Loading state for API calls
  const [currentMedicalId, setCurrentMedicalId] = useState(null); // ID of medical center being edited
  const [showModal, setShowModal] = useState(false); // Controls visibility of add/edit modal

  // Filter state
  const [filterForm, setFilterForm] = useState({
    name: '', // Filter by medical center name
    // location_id: '', // Removed location_id filters
    type: '' // Filter by medical center type
  }); // Filter criteria for medical centers

  // Pagination state
  const [page, setPage] = useState(1); // Current page number
  const limit = 10; // Number of items per page
  const [total, setTotal] = useState(0); // Total number of medical centers

  /**
   * Fetches medical centers from the API with pagination and filtering
   */
  const fetchMedicalCenters = async () => {
    try {
      setIsLoading(true); // Set loading state
      const params = {
        page,
        limit
      };

      if (filterForm.name) params.name = filterForm.name; // Add name filter if provided
      if (filterForm.type) params.type = filterForm.type; // Add type filter if provided
      // if (filterForm.location_id) params.location_id = filterForm.location_id; // Removed location_id filter

      const response = await axiosInstance.get('/ems/medical-centers', { params }); // Fetch medical centers

      const data = response.data?.data || response.data || []; // Handle different response formats
      setMedicalCenters(data); // Update medical centers state
      setTotal(response.data?.total || data.length || 0); // Update total count
    } catch (error) {
      console.error('Error fetching medical centers:', error); // Log error
      alert('Failed to fetch medical centers'); // Show error alert
      setMedicalCenters([]); // Reset medical centers
    } finally {
      setIsLoading(false); // Clear loading state
    }
  };

  // Fetch medical centers when filterForm or page changes
  useEffect(() => {
    fetchMedicalCenters();
  }, [filterForm, page]); // Re-run when filterForm or page changes

  /**
   * Handles input changes for the form
   * @param {Object} e - The event object
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target; // Extract name and value
    setFormData((prev) => ({ ...prev, [name]: value })); // Update form data
  };

  /**
   * Handles filter input changes
   * @param {Object} e - The event object
   */
  const handleFilterChange = (e) => {
    const { name, value } = e.target; // Extract name and value
    setFilterForm(prev => ({ ...prev, [name]: value })); // Update filter form
  };

  /**
   * Clears all filters and resets pagination
   */
  const clearFilters = () => {
    setFilterForm({
      name: '',
      // location_id: '', // Removed location_id from clear
      type: ''
    }); // Reset filter fields
    setPage(1); // Reset to first page
  };

  /**
   * Opens the modal for adding a new medical center
   * Resets form data and sets edit mode to false
   */
  const openAddModal = () => {
    setIsEditMode(false); // Set to add mode
    setFormData({
      id: '',
      name: '',
      type: '',
      // location_id: '', // Removed location_id from formData
      // area_id: ''       // Removed area_id from formData
    }); // Reset form data
    setShowModal(true); // Show modal
  };

  /**
   * Prepares the modal for editing an existing medical center
   * @param {Object} medicalCenter - The medical center data to edit
   */
  const handleEdit = (medicalCenter) => {
    setIsEditMode(true); // Set to edit mode
    setCurrentMedicalId(medicalCenter.id); // Set medical center ID

    // Removed session storage for location_id and area_id
    // sessionStorage.setItem('location_id', medicalCenter.location_id);
    // sessionStorage.setItem('area_id', medicalCenter.area_id || '');

    setFormData({
      id: medicalCenter.id,
      name: medicalCenter.name,
      type: medicalCenter.type,
      // location_id: medicalCenter.location_id, // Removed
      // area_id: medicalCenter.area_id || '',   // Removed
      longitude: medicalCenter.longitude?.toString() || '',
      latitude: medicalCenter.latitude?.toString() || ''
    }); // Populate form with medical center data

    setShowModal(true); // Show modal
  };

  /**
   * Saves or updates a medical center
   */
  const handleSave = async () => {
    try {
      setIsLoading(true); // Set loading state
      if (isEditMode) {
        await axiosInstance.put(`/ems/medical-centers/${currentMedicalId}`, formData); // Update medical center
        alert('Medical center updated successfully!'); // Show success alert
      } else {
        await axiosInstance.post('/ems/medical-centers', formData); // Create medical center
        alert('Medical center added successfully!'); // Show success alert
      }
      setShowModal(false); // Close modal
      fetchMedicalCenters(); // Refresh medical centers list
    } catch (error) {
      console.error('Save failed:', error); // Log error
      alert(error.response?.data?.message || 'Failed to save medical center'); // Show error alert
    } finally {
      setIsLoading(false); // Clear loading state
    }
  };

  /**
   * Deletes a medical center after confirmation
   * @param {number} id - The ID of the medical center to delete
   */
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this medical center?')) {
      try {
        setIsLoading(true); // Set loading state
        await axiosInstance.delete(`/ems/medical-centers/${id}`); // Delete medical center
        alert('Medical center deleted successfully!'); // Show success alert
        fetchMedicalCenters(); // Refresh medical centers list
      } catch (error) {
        console.error('Delete failed:', error); // Log error
        alert('Failed to delete medical center'); // Show error alert
      } finally {
        setIsLoading(false); // Clear loading state
      }
    }
  };

  /**
   * Closes the modal and cleans up
   */
  const handleModalClose = () => {
    setShowModal(false); // Hide modal
    // Removed session clear for location_id and area_id
    // sessionStorage.removeItem('location_id');
    // sessionStorage.removeItem('area_id');
  };

  // Render the component
  return (
    <div className="tab-pane active" id="bs-medical" role="tabpanel">
      <div className="filter-search mt-3">
        <h6 className="px-2 mb-0">Filter</h6>
        <div className="filter-panel">
          <form className="row">
            <div className="form-group col-md-4 col-lg-4 col-12 p-2">
              <input 
                type="text" 
                className="form-control" 
                name="name"
                placeholder="Search By Name" 
                value={filterForm.name}
                onChange={handleFilterChange}
              />
            </div>
            <div className="form-group col-md-4 col-lg-4 col-12 p-2">
              <select
                className="form-control form-select"
                name="type"
                value={filterForm.type}
                onChange={handleFilterChange}
              >
                <option value="">All Types</option>
                <option value="Hospital">Hospital</option>
                <option value="Center">Center</option>
                <option value="Clinic">Clinic</option>
              </select>
            </div>
            {/* Removed Location Dropdown */}
            <div className="form-group col-lg-3 p-2">
              <button 
                type="button" 
                className="btn btn-outline-primary btn-rounded"
                onClick={clearFilters}
              >
                Clear Filter
              </button>
            </div>
          </form>
          <button
            className="btn m-2 d-flex align-items-center justify-content-center btn-outline-primary"
            onClick={openAddModal}
          >
            <i className="ti ti-circle-plus fs-4 me-2"></i>
            Add Medical Center
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-4">Loading medical centers...</div>
      ) : (
        <div className="py-3 px-0">
          <div className="table-responsive rounded-2 ros-table">
            <table className="table text-nowrap customize-table mb-0 align-middle">
              <thead className="text-dark fs-4">
                <tr>
                  <th><h6 className="fw-semibold mb-0">ID</h6></th>
                  <th><h6 className="fw-semibold mb-0">Name</h6></th>
                  <th><h6 className="fw-semibold mb-0">Type</h6></th>
                  <th><h6 className="fw-semibold mb-0">Latitude</h6></th>
                  <th><h6 className="fw-semibold mb-0">Longitude</h6></th>
                  {/* Removed Location and Area columns */}
                  <th><h6 className="fw-semibold mb-0">Action</h6></th>
                </tr>
              </thead>
              <tbody>
                {medicalCenters.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      No Medical Centers Found
                    </td>
                  </tr>
                ) : (
                  medicalCenters.map((medCenter, index) => (
                    <tr key={medCenter.id}>
                      <td>{(page - 1) * limit + index + 1}</td> {/* Calculate display ID */}
                      <td>{medCenter.name}</td>
                      <td>{medCenter.type || 'N/A'}</td>
                      <td>{medCenter.latitude || 'N/A'}</td>
                      <td>{medCenter.longitude || 'N/A'}</td>
                      {/* Removed Location and Area Data */}
                      <td>
                        <button
                          className="h4 me-3 border-0 bg-transparent"
                          onClick={() => handleEdit(medCenter)}
                          aria-label="Edit medical center"
                        >
                          <i className="ti ti-edit text-warning"></i>
                        </button>
                        <button
                          className="h4 border-0 bg-transparent"
                          onClick={() => handleDelete(medCenter.id)}
                          aria-label="Delete medical center"
                        >
                          <i className="ti ti-trash text-danger"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {medicalCenters.length > 0 && (
            <Pagination
              total={total}
              limit={limit}
              page={page}
              onPageChange={setPage}
            />
          )}

          <MedicalAddModal
            show={showModal}
            onClose={handleModalClose}
            formData={formData}
            onChange={handleInputChange}
            onSave={handleSave}
            isEditMode={isEditMode}
            initialData={isEditMode ? formData : {}}
            isLoading={isLoading}
          />
        </div>
      )}
    </div>
  );
};

// Export the component
export default MedicalTab;